<template>
  <label
    :class="[
      'cms-radio',
      {
        'is-checked': isChecked,
        'is-disabled': isDisabled,
        'is-bordered': border,
        [`cms-radio--${radioSize}`]: border && radioSize
      }
    ]"
  >
    <span
      class="cms-radio__input"
      :class="{
        'is-checked': isChecked,
        'is-disabled': isDisabled
      }"
    >
      <input
        ref="radioRef"
        v-model="model"
        class="cms-radio__original"
        type="radio"
        :name="name || radioGroup?.name"
        :disabled="isDisabled"
        :value="label"
        @change="handleChange"
      >
      <span class="cms-radio__inner"></span>
    </span>
    <span class="cms-radio__label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script lang="ts" setup>
import { ref, computed, inject } from 'vue'
import { radioProps, radioEmits, radioGroupKey } from './radio'

defineOptions({
  name: 'CmsRadio'
})

const props = defineProps(radioProps)
const emit = defineEmits(radioEmits)

// 注入 RadioGroup 上下文
const radioGroup = inject(radioGroupKey, undefined)

// 单选框引用
const radioRef = ref<HTMLInputElement | null>(null)

// 计算尺寸
const radioSize = computed(() => props.size || radioGroup?.size.value || '')

// 计算是否禁用
const isDisabled = computed(() => props.disabled || radioGroup?.disabled.value || false)

// 计算是否选中
const isChecked = computed(() => model.value === props.label)

// 计算绑定值
const model = computed({
  get() {
    return radioGroup ? radioGroup.modelValue.value : props.modelValue
  },
  set(val: string | number | boolean) {
    if (radioGroup) {
      radioGroup.onChange(val)
    } else {
      emit('update:modelValue', val)
    }
    radioRef.value && (radioRef.value.checked = isChecked.value)
  }
})

// 处理变更事件
const handleChange = () => {
  emit('change', model.value)
}
</script>

<style>
.cms-radio {
  --cms-radio-font-size: 14px;
  --cms-radio-input-height: 14px;
  --cms-radio-input-width: 14px;
  --cms-radio-input-border-radius: 100%;
  --cms-radio-input-bg-color: #fff;
  --cms-radio-input-border: 1px solid #dcdfe6;
  --cms-radio-input-border-color: #dcdfe6;
  --cms-radio-checked-text-color: #409eff;
  --cms-radio-checked-input-border-color: #409eff;
  --cms-radio-checked-input-bg-color: #409eff;
  --cms-radio-disabled-input-border-color: #e4e7ed;
  --cms-radio-disabled-input-bg-color: #f5f7fa;
  --cms-radio-disabled-icon-color: #c0c4cc;
  --cms-radio-disabled-checked-input-bg-color: #f5f7fa;
  --cms-radio-disabled-checked-input-border-color: #e4e7ed;
  --cms-radio-disabled-checked-icon-color: #c0c4cc;
  
  color: #606266;
  font-weight: 400;
  font-size: var(--cms-radio-font-size);
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  outline: none;
  user-select: none;
  margin-right: 30px;
  height: 32px;
}

.cms-radio.is-bordered {
  padding: 0 15px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  box-sizing: border-box;
}

.cms-radio.is-bordered.is-checked {
  border-color: #409eff;
}

.cms-radio.is-bordered.is-disabled {
  cursor: not-allowed;
  border-color: #ebeef5;
}

.cms-radio.is-bordered.cms-radio--large {
  padding: 0 19px;
  border-radius: 4px;
  height: 40px;
}

.cms-radio.is-bordered.cms-radio--large .cms-radio__label {
  font-size: 16px;
}

.cms-radio.is-bordered.cms-radio--small {
  padding: 0 11px;
  border-radius: 3px;
  height: 24px;
}

.cms-radio.is-bordered.cms-radio--small .cms-radio__label {
  font-size: 12px;
}

.cms-radio__input {
  white-space: nowrap;
  cursor: pointer;
  outline: none;
  display: inline-flex;
  position: relative;
  vertical-align: middle;
}

.cms-radio__input.is-disabled .cms-radio__inner {
  background-color: var(--cms-radio-disabled-input-bg-color);
  border-color: var(--cms-radio-disabled-input-border-color);
  cursor: not-allowed;
}

.cms-radio__input.is-disabled.is-checked .cms-radio__inner {
  background-color: var(--cms-radio-disabled-checked-input-bg-color);
  border-color: var(--cms-radio-disabled-checked-input-border-color);
}

.cms-radio__input.is-disabled.is-checked .cms-radio__inner::after {
  background-color: var(--cms-radio-disabled-checked-icon-color);
}

.cms-radio__input.is-disabled + .cms-radio__label {
  color: #c0c4cc;
  cursor: not-allowed;
}

.cms-radio__input.is-checked .cms-radio__inner {
  border-color: var(--cms-radio-checked-input-border-color);
  background: var(--cms-radio-checked-input-bg-color);
}

.cms-radio__input.is-checked .cms-radio__inner::after {
  transform: translate(-50%, -50%) scale(1);
}

.cms-radio__input.is-checked + .cms-radio__label {
  color: var(--cms-radio-checked-text-color);
}

.cms-radio__inner {
  border: var(--cms-radio-input-border);
  border-radius: var(--cms-radio-input-border-radius);
  width: var(--cms-radio-input-width);
  height: var(--cms-radio-input-height);
  background-color: var(--cms-radio-input-bg-color);
  position: relative;
  cursor: pointer;
  display: inline-block;
  box-sizing: border-box;
}

.cms-radio__inner::after {
  width: 4px;
  height: 4px;
  border-radius: var(--cms-radio-input-border-radius);
  background-color: var(--cms-radio-input-bg-color);
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: transform 0.15s ease-in;
}

.cms-radio__original {
  opacity: 0;
  outline: none;
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
}

.cms-radio__label {
  font-size: var(--cms-radio-font-size);
  padding-left: 8px;
}

.cms-radio:hover:not(.is-disabled) .cms-radio__inner {
  border-color: var(--cms-radio-checked-input-border-color);
}
</style>